import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import { isGroupedPoll } from '@tunasong/schemas'
import { FormControl, FormGroup, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import {
  countGroupPicks,
  formatGroupStatus,
  getGroupOptionIndices,
  getEffectiveQuota,
  getVotableGroups,
  isCompleteGroupedResponse,
  pickInGroup,
} from '../groups.js'
import { useManyOfManyResponse } from '../many-of-many/use-many-of-many-response.js'
import { PlayAllButton, PollAudioProvider } from '../option-audio.js'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { sortByRatings, sortPollOptionsIndices } from '../sort.js'
import { VoteOptionRow } from '../vote-option-row.js'
import { GroupSection } from './group-section.js'
import { GroupedPollResult } from './result.js'

/**
 * A many-of-many poll in groups — e.g. the best song on each album. Each group has its own quota; a quota-1 group
 * behaves like a radio, so picking another option moves the pick. Answers save as they are picked.
 */
export const GroupedPollVote: FC<PollProps> = props => {
  const { poll, userId, onChange, sort } = props

  invariant(isGroupedPoll(poll), 'Poll must be a grouped poll')

  const { userId: currentUserId } = useCurrentUser()
  const { profile: respondent } = graphHooks.useProfile(userId)
  const response = useManyOfManyResponse(poll, userId)
  const { allResponses, hasLoadedChildren, isExternalRespondent, choices, ratings, comments } = response

  const { name = 'Untitled', closed } = poll
  const readOnly = Boolean(!onChange || closed || !hasLoadedChildren || isExternalRespondent)

  const groups = getVotableGroups(poll)
  const finishedGroups = groups.filter(
    group => countGroupPicks(poll, choices, group.id) === getEffectiveQuota(poll, group)
  ).length
  const isComplete = isCompleteGroupedResponse(poll, choices)

  /** One seeded shuffle for the whole poll, filtered per group: the order holds, and never crosses groups. */
  const baseOrder = sort
    ? sortPollOptionsIndices(poll, allResponses, 'random', `${currentUserId ?? userId}:${poll.id}`)
    : poll.pollOptions.map((_, idx) => idx)

  const handleVote = (idx: number) => (value: boolean) => response.setChoices(pickInGroup(poll, choices, idx, value))

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography variant="subtitle1" className={styles.title}>
          <PollIcon /> {name}
        </Typography>
        <PlayAllButton options={poll.pollOptions} />
        {closed ? (
          <>
            <Typography variant="caption" color="secondary">
              The Poll is closed.
            </Typography>
            <GroupedPollResult poll={poll} responses={allResponses} />
          </>
        ) : null}
        {!isExternalRespondent && !closed && !isComplete ? (
          <Typography variant="caption" display="block">
            You have picked in {finishedGroups} of {groups.length} groups.
          </Typography>
        ) : null}
        {isComplete ? (
          <Typography variant="caption" color="primary" display="block">
            {isExternalRespondent ? (respondent?.name ?? respondent?.email + ' has') : 'You have'} completed this poll.
          </Typography>
        ) : null}
        {closed ? null : (
          <FormControl className={styles.formControl}>
            <FormGroup>
              {hasLoadedChildren
                ? groups.map(group => {
                    const indices = getGroupOptionIndices(poll, group.id)
                    const quota = getEffectiveQuota(poll, group)
                    const picked = countGroupPicks(poll, choices, group.id)
                    const groupOrder = baseOrder.filter(idx => indices.includes(idx))
                    const order = readOnly ? groupOrder : sortByRatings(groupOrder, ratings, choices)
                    return (
                      <GroupSection
                        key={group.id}
                        name={group.name}
                        status={readOnly ? undefined : formatGroupStatus(picked, quota)}
                        order={order}
                      >
                        {order.map(idx => (
                          <VoteOptionRow
                            key={idx}
                            idx={idx}
                            option={poll.pollOptions[idx]}
                            picked={choices[idx] ?? false}
                            readOnly={readOnly}
                            voteDisabled={readOnly || (quota > 1 && picked >= quota && !choices[idx])}
                            showComment={!isExternalRespondent}
                            rating={ratings[idx]}
                            comment={comments[idx]}
                            onVote={handleVote(idx)}
                            onRate={rating => response.rate(idx, rating)}
                            onComment={text => response.comment(idx, text)}
                          />
                        ))}
                      </GroupSection>
                    )
                  })
                : null}
            </FormGroup>
            {!readOnly ? (
              <Typography variant="caption" color="textSecondary">
                Your choices are saved as you pick them.
              </Typography>
            ) : null}
          </FormControl>
        )}
      </div>
    </PollAudioProvider>
  )
}

export default GroupedPollVote
